import { useElementSize } from '@mantine/hooks'
import { Divider, Stack } from '@mui/material'
import { useLocation } from 'react-router-dom'

import ScrollToElement from '~/components/scrollToElement'
import LoadingBubbles from '~/features/chatbot/components/dialog/loadingBubbles'
import {
  TEMPLATE_PAGE_CHAT_KEY,
  TEMPLATE_PAGE_CHAT_VALUE,
} from '~/features/chatbot/constants'
import ChatDialogLoadingSkeleton from '~/features/chatbotV2/components/dialog/chatDialogLoadingSkeleton'
import ChatDialogMessage from '~/features/chatbotV2/components/dialog/chatDialogMessage'
import DialogInput from '~/features/chatbotV2/components/dialog/dialogInput'
import { TChatV2Params } from '~/features/chatbotV2/hooks/useChatV2'
import { store } from '~/store'
import rem from '~/utils/ui'

interface TProps {
  mutate: (params: TChatV2Params) => void
  isPending: boolean
  hasSessionEnded: boolean
  guestToken: string | null
  refreshGuestToken: () => Promise<string | null>
  isSessionReady: boolean
  isWaitingForChatReady: boolean
  sendTyping: () => void
}

// Clone of features/chatbot/components/dialog (v1) — pending state comes in
// as a prop instead of react-query's useIsMutating, since v2's mutate isn't
// a react-query mutation (see components/simple for why).
function Dialog({
  mutate,
  isPending,
  hasSessionEnded,
  guestToken,
  refreshGuestToken,
  isSessionReady,
  isWaitingForChatReady,
  sendTyping,
}: TProps) {
  const { ref: dialogInputRef } = useElementSize()

  const messages = store((state) => state.chatbot.messages)

  const location = useLocation()
  const isChatbotOnTemplatePage =
    new URLSearchParams(location.search).get(TEMPLATE_PAGE_CHAT_KEY) ===
    TEMPLATE_PAGE_CHAT_VALUE

  if (isWaitingForChatReady) {
    return <ChatDialogLoadingSkeleton />
  }

  return (
    <Stack
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Stack
        gap={1}
        sx={{ padding: rem(16), overflowY: 'auto', flex: 1, minHeight: 0 }}
      >
        {messages.map((dialog, index) => (
          // Index is stable here — v2's messages array is append-only /
          // mutated in place by index (see useChatV2), never reordered or
          // spliced, so index-as-key is safe. Using nanoid() (as v1 does)
          // would regenerate a new key on every render, remounting the
          // entire list on every STREAM_CHUNK update.
          <ChatDialogMessage
            key={`message-${index.toString()}`}
            chatMessage={dialog}
          />
        ))}
        {!isChatbotOnTemplatePage && <ScrollToElement />}
        {isPending && <LoadingBubbles />}
      </Stack>
      <Divider
        sx={{
          marginBottom: rem(12),
          marginTop: 'auto',
        }}
      />
      <DialogInput
        ref={dialogInputRef}
        mutate={mutate}
        isPending={isPending}
        hasSessionEnded={hasSessionEnded}
        guestToken={guestToken}
        refreshGuestToken={refreshGuestToken}
        isSessionReady={isSessionReady}
        sendTyping={sendTyping}
      />
    </Stack>
  )
}

export default Dialog
